// apps/admin/src/pages/billing/CreditMemoApprovalsPage.tsx
//
// Phase 8.2 Wave 3 Task 19 — Pending Approval Queue page.
//
// vg-binding: CONTEXT:decisions=P8.D-496,P8.D-490,P8.D-495
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per CONTEXT.md (Amendment #3) — NEW page at
// `/admin/billing/credit-memo-approvals` listing credit_memos with status
// `PENDING_APPROVAL`. Sortable by created_at desc. Per-row:
//   - Merchant (name + email + id)
//   - Amount in USD (highlighted in rose when > $500 trigger)
//   - Issued by (kế toán user)
//   - Issued at (date)
//   - Notes (truncated)
//   - Actions: Approve (admin only, `credit_memo:approve`) + Reject (with
//     required reason 20-1000 chars → BE emails kế toán, audit log)
//
// Filter chips ("Bulk filter"):
//   - Merchant (typed merchant id; full picker comes in a follow-up task)
//   - Issued-by user (typed user id)
//   - Amount range (min/max USD)
//
// Endpoints consumed (API-CONTRACTS):
//   GET  /api/v1/admin/credit-memos?status=pending_approval&...
//   POST /api/v1/admin/credit-memos/:id/approve (handled by ActionsCell)
//   POST /api/v1/admin/credit-memos/:id/reject  (handled by ActionsCell)
//
// Permission gate: The list itself is visible to anyone with
// `credit_memo:read`. The per-row Approve button is HIDDEN for users without
// `credit_memo:approve`. The Reject button currently mirrors the same gate
// (admin-only) per CONTEXT — "Approve (admin only)" is the explicit
// language but Reject is a same-quorum decision so we require the same perm
// to keep the SoD model consistent (kế toán who issued the memo cannot also
// reject their own — that lives in BE Block 1 auth per the API contract).
//
// Audit log: BE writes audit_log entries on approve + reject
// FE never writes audit log directly.
//
// Test IDs (exact strings per task brief):
//   credit-memo-approval-row-{memoId}
//   credit-memo-approve-btn-{memoId}      (in ActionsCell)
//   credit-memo-reject-btn-{memoId}       (in ActionsCell)
//   credit-memo-reject-modal              (in ActionsCell)
//   credit-memo-reject-reason-input       (in ActionsCell)

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  UserPicker,
  cn,
  useAuth,
  useToast,
  PageHeader,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import { CreditMemoApprovalActionsCell } from '../../features/billing/CreditMemoApprovalActionsCell.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 25;

/**  trigger threshold — amounts above this get highlighted red
 *  in the queue (the BE put them into PENDING_APPROVAL specifically because
 *  they crossed this line for accountant-issued memos). */
const HIGHLIGHT_THRESHOLD_CENTS = 50_000; // $500.00

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of GET /admin/credit-memos response shape.
// Mirrors the Phase 4.1 list endpoint which P8.2 reuses with
// `?status=pending_approval`. Hand-rolled because shared/api codegen lands
// at /vg:test.
// ---------------------------------------------------------------------------

interface PendingApprovalRow {
  id: string;
  merchant_id: string;
  merchant_name?: string | null;
  merchant_email?: string | null;
  amount_usd_cents: number;
  status: string;
  issue_type: string;
  notes?: string | null;
  /** kế toán user that issued the memo — populated by the Phase 4.1 issue
   *  handler (created_by) and surfaced by the list endpoint after Phase 8.2
   *  Wave 1 schema widen. Falls back to `—` when absent. */
  created_by?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_at: string;
}

interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface PendingApprovalListResponseBody {
  credit_memos: PendingApprovalRow[];
  pagination: PaginationMeta;
}

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: { code?: string; message?: string; user_message?: string };
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(cents) / 100);
}

function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CreditMemoApprovalsPage(): JSX.Element {
  const { t } = useTranslation('finance');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuth((s) => s.user);

  //  perm gate. The auth hook does not (yet) expose a permission
  // list directly, so we fall back to a primaryRole check — admin OR
  // superadmin can approve/reject. Once `useAuth` exposes `permissions[]`
  // this should switch to `permissions.includes('credit_memo:approve')`
  // without altering the surface contract here.
  const isAdmin =
    user?.primaryRole === 'admin' || user?.primaryRole === 'superadmin';
  const canApprove = isAdmin;

  // ---- URL state ----
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE cap.
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE),
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : DEFAULT_PAGE_SIZE;
  const merchantId = searchParams.get('merchant_id') ?? '';
  const issuedBy = searchParams.get('issued_by') ?? '';
  const amountMinRaw = searchParams.get('amount_min') ?? '';
  const amountMaxRaw = searchParams.get('amount_max') ?? '';

  // ---- Local input state for amount range only (commit on blur / Enter so
  //      the URL doesn't thrash on every keystroke). Merchant + Issued-by
  //      are pickers — commit instantly on pick. ----
  const [amountMinInput, setAmountMinInput] = useState(amountMinRaw);
  const [amountMaxInput, setAmountMaxInput] = useState(amountMaxRaw);

  // Picker label state — survives across mounts and shows the picked entity
  // name on the trigger button without an extra BE lookup.
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [issuedByLabel, setIssuedByLabel] = useState<string | null>(null);

  // Sync inputs when URL changes externally (e.g. back/forward).
  useEffect(() => setAmountMinInput(amountMinRaw), [amountMinRaw]);
  useEffect(() => setAmountMaxInput(amountMaxRaw), [amountMaxRaw]);
  // Clear label when filter is cleared externally.
  useEffect(() => {
    if (!merchantId) setMerchantLabel(null);
  }, [merchantId]);
  useEffect(() => {
    if (!issuedBy) setIssuedByLabel(null);
  }, [issuedBy]);

  // ---- List state ----
  const [rows, setRows] = useState<PendingApprovalRow[]>([]);
  const [pagination, setPaginationState] = useState<PaginationMeta>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // ---- URL helpers ----
  const updateParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(searchParams);
      if (value === '' || value === undefined) next.delete(key);
      else next.set(key, value);
      // Any filter change resets page to 1.
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (n: number): void => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete('page');
      else next.set('page', String(n));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setMerchantLabel(null);
    setIssuedByLabel(null);
    setAmountMinInput('');
    setAmountMaxInput('');
  }, [setSearchParams]);

  const hasAnyFilter = !!(merchantId || issuedBy || amountMinRaw || amountMaxRaw);

  // ---- List fetch ----
  // BE `GET /api/v1/admin/credit-memos` schema (issue.handler.ts L1115-L1126)
  // is `additionalProperties: false` and the handler hard-codes
  // `sort({ createdAt: -1 })`. Only the supported params are sent on the wire;
  // `issued_by`, amount range, and `sort` are applied client-side below until
  // BE adds those filters.
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    // vg:debug F-011: BE zod schema rejects lowercase. Canonical enum is
    // UPPERCASE PENDING_APPROVAL (matches CreditMemoListPage + Phase 4.1
    // service constant). CONTEXT prose used lowercase by mistake.
    p.set('status', 'PENDING_APPROVAL');
    p.set('page', String(page));
    p.set('pageSize', String(pageSize));
    if (merchantId) p.set('merchant_id', merchantId);
    return p.toString();
  }, [page, pageSize, merchantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const { data, error, response } = await client.GET(
          `/api/v1/admin/credit-memos?${queryString}`,
        );
        if (cancelled) return;
        if (error || !response.ok) {
          toast({
            title: t('credit_memo.approvalsPage.loadFailed'),
            description: extractApiMessage(error, t('credit_memo.toast.genericError')),
            variant: 'error',
          });
          setRows([]);
          setPaginationState({
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
          return;
        }
        // Envelope unwrap — `{ok:true, data:{credit_memos, pagination}}` OR
        // legacy bare body. Mirror Phase 4.1 CreditMemoListPage pattern.
        const body = data as
          | { ok?: true; data?: PendingApprovalListResponseBody }
          | PendingApprovalListResponseBody
          | undefined;
        const inner: PendingApprovalListResponseBody | undefined =
          body && typeof body === 'object' && 'data' in body && body.data
            ? body.data
            : (body as PendingApprovalListResponseBody | undefined);
        if (inner && Array.isArray(inner.credit_memos)) {
          // Defensive client-side sort by created_at desc — guarantees the
          // "Sortable by created_at desc" acceptance criterion holds even
          // if the BE didn't honor `?sort=created_at:desc`.
          const sorted = [...inner.credit_memos].sort((a, b) => {
            const ta = a.created_at ? Date.parse(a.created_at) : 0;
            const tb = b.created_at ? Date.parse(b.created_at) : 0;
            return tb - ta;
          });
          setRows(sorted);
          setPaginationState(
            inner.pagination ?? {
              page,
              pageSize: DEFAULT_PAGE_SIZE,
              total: sorted.length,
              totalPages: 1,
            },
          );
        } else {
          setRows([]);
          setPaginationState({
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
        }
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('credit_memo.toast.networkError'),
          description:
            err instanceof Error && err.message
              ? err.message
              : t('credit_memo.toast.networkErrorDesc'),
          variant: 'error',
        });
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryString, refreshTick, toast, page, t]);

  const handleResolved = useCallback((): void => {
    setRefreshTick((t) => t + 1);
  }, []);

  // debug-bill iter 22 — client-side filter for issued_by + amount range.
  // BE doesn't yet accept these query params; apply on the in-memory rows
  // so the FE filter chips actually filter the displayed list.
  const visibleRows = useMemo(() => {
    let out = rows;
    if (issuedBy) {
      out = out.filter((r) => {
        const issuer =
          (r as unknown as { issued_by?: string; created_by?: string })
            .issued_by ??
          (r as unknown as { created_by?: string }).created_by ??
          '';
        return issuer === issuedBy;
      });
    }
    const minCents = amountMinRaw ? Number(amountMinRaw) * 100 : null;
    const maxCents = amountMaxRaw ? Number(amountMaxRaw) * 100 : null;
    if (minCents != null && Number.isFinite(minCents)) {
      out = out.filter((r) => {
        const amt =
          (r as unknown as { amount_usd_cents?: number; original_amount_usd_cents?: number })
            .amount_usd_cents ??
          (r as unknown as { original_amount_usd_cents?: number })
            .original_amount_usd_cents ??
          0;
        return amt >= minCents;
      });
    }
    if (maxCents != null && Number.isFinite(maxCents)) {
      out = out.filter((r) => {
        const amt =
          (r as unknown as { amount_usd_cents?: number; original_amount_usd_cents?: number })
            .amount_usd_cents ??
          (r as unknown as { original_amount_usd_cents?: number })
            .original_amount_usd_cents ??
          0;
        return amt <= maxCents;
      });
    }
    return out;
  }, [rows, issuedBy, amountMinRaw, amountMaxRaw]);

  // ---- Render ----
  return (
    <div
      className="flex flex-col gap-6 p-6"
      data-testid="credit-memo-approvals-page"
    >
      {/* Page header — debug-bill iter 9: "All credit memos" back-link
        * removed; sidebar already exposes both "Credit Memos" (full
        * list) and "Pending Approvals" (this page) as separate entries
        * under the Billing group, so a header CTA is redundant. */}
      <PageHeader
        title={t('credit_memo.approvalsPage.title')}
        subtitle={t('credit_memo.approvalsPage.subtitle')}
      />

      {!isAdmin ? (
        <div
          role="alert"
          className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-300"
          data-testid="credit-memo-approvals-readonly-banner"
        >
          {t('credit_memo.approvalsPage.readonlyPrefix')}{' '}
          <code>credit_memo:approve</code>{' '}
          {t('credit_memo.approvalsPage.readonlySuffix')}
        </div>
      ) : null}

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="min-w-[220px] flex-1">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="cma-merchant-filter"
            >
              {t('credit_memo.columns.merchant')}
            </label>
            <MerchantPicker
              value={merchantId || null}
              valueLabel={merchantLabel}
              onChange={(id, label) => {
                setMerchantLabel(label);
                updateParam('merchant_id', id ?? '');
              }}
              placeholder={t('credit_memo.filters.allMerchants')}
              testId="cma-merchant-filter"
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="cma-issued-by-filter"
            >
              {t('credit_memo.approvalsPage.issuedByFilterLabel')}
            </label>
            <UserPicker
              value={issuedBy || null}
              valueLabel={issuedByLabel}
              onChange={(id, label) => {
                setIssuedByLabel(label);
                updateParam('issued_by', id ?? '');
              }}
              roleType="accountant"
              placeholder={t('credit_memo.approvalsPage.allAccountants')}
              testId="cma-issued-by-filter"
            />
          </div>
          <div className="min-w-[140px]">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="cma-amount-min"
            >
              {t('credit_memo.approvalsPage.amountMin')}
            </label>
            <Input
              id="cma-amount-min"
              data-testid="cma-amount-min"
              type="number"
              min="0"
              step="0.01"
              value={amountMinInput}
              placeholder="0.00"
              onChange={(e) => setAmountMinInput(e.target.value)}
              onBlur={() => {
                if (amountMinInput.trim() !== amountMinRaw) {
                  updateParam('amount_min', amountMinInput.trim());
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  updateParam('amount_min', amountMinInput.trim());
              }}
            />
          </div>
          <div className="min-w-[140px]">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="cma-amount-max"
            >
              {t('credit_memo.approvalsPage.amountMax')}
            </label>
            <Input
              id="cma-amount-max"
              data-testid="cma-amount-max"
              type="number"
              min="0"
              step="0.01"
              value={amountMaxInput}
              placeholder="∞"
              onChange={(e) => setAmountMaxInput(e.target.value)}
              onBlur={() => {
                if (amountMaxInput.trim() !== amountMaxRaw) {
                  updateParam('amount_max', amountMaxInput.trim());
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  updateParam('amount_max', amountMaxInput.trim());
              }}
            />
          </div>
          {hasAnyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t('credit_memo.filters.clear')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table card */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="credit-memo-approvals-table"
            >
              <thead className="border-b border-pw-border bg-pw-surface/60 text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-4 py-3">{t('credit_memo.columns.merchant')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.amount')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.issuedBy')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.issuedAt')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.notes')}</th>
                  <th className="w-[180px] px-4 py-3 text-right">
                    {t('credit_memo.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('credit_memo.approvalsPage.loading')}
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {hasAnyFilter
                        ? t('credit_memo.approvalsPage.emptyFiltered')
                        : t('credit_memo.approvalsPage.emptyNone')}
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row) => {
                    const highlightAmount =
                      row.amount_usd_cents > HIGHLIGHT_THRESHOLD_CENTS;
                    const issuedByLabel =
                      row.created_by_name ||
                      row.created_by_email ||
                      row.created_by ||
                      '—';
                    const merchantLabel =
                      row.merchant_name || row.merchant_email || row.merchant_id;
                    return (
                      <tr
                        key={row.id}
                        data-testid={`credit-memo-approval-row-${row.id}`}
                        data-memo-id={row.id}
                        className="border-b border-pw-border last:border-b-0 transition-colors hover:bg-pw-surface/50"
                      >
                        <td className="px-4 py-4 align-top">
                          <div
                            className="font-semibold text-pw-text"
                            title={row.merchant_id}
                          >
                            {row.merchant_name || '—'}
                          </div>
                          {row.merchant_email ? (
                            <div className="text-xs text-pw-text-muted">
                              {row.merchant_email}
                            </div>
                          ) : null}
                          <div className="font-mono text-[10px] text-pw-text-muted/70">
                            …{row.merchant_id.slice(-6)}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span
                            className={cn(
                              'font-semibold tabular-nums',
                              highlightAmount
                                ? 'text-rose-600 dark:text-rose-300'
                                : 'text-pw-text',
                            )}
                            title={
                              highlightAmount
                                ? t('credit_memo.approvalsPage.overThresholdTooltip')
                                : undefined
                            }
                            data-highlight={
                              highlightAmount ? 'over-threshold' : 'normal'
                            }
                          >
                            {formatUsd(row.amount_usd_cents)}
                          </span>
                        </td>
                        <td
                          className="px-4 py-4 align-top text-pw-text"
                          title={row.created_by ?? undefined}
                        >
                          <div className="text-sm">{issuedByLabel}</div>
                          {row.created_by &&
                          row.created_by !== issuedByLabel ? (
                            <div className="font-mono text-[10px] text-pw-text-muted/70">
                              …{row.created_by.slice(-6)}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-4 align-top text-pw-text">
                          {formatDateOnly(row.created_at)}
                        </td>
                        <td
                          className="max-w-[280px] px-4 py-4 align-top text-pw-text-muted"
                          title={row.notes ?? undefined}
                        >
                          <div className="line-clamp-3 whitespace-pre-wrap break-words text-sm">
                            {row.notes || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-right">
                          <CreditMemoApprovalActionsCell
                            memoId={row.id}
                            merchantLabel={merchantLabel}
                            amountUsdCents={row.amount_usd_cents}
                            canApprove={canApprove}
                            onResolved={handleResolved}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          <div className="border-t border-pw-border px-6 py-4">
            <PageListFooter
              page={pagination.page || page}
              pageSize={pageSize}
              total={pagination.total}
              totalPages={Math.max(1, pagination.totalPages || 1)}
              loading={loading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                const next = new URLSearchParams(searchParams);
                next.set('pageSize', String(size));
                next.delete('page');
                setSearchParams(next, { replace: true });
              }}
              testId="credit-memo-approvals-footer"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default CreditMemoApprovalsPage;
